import { useCallback, useEffect, useState } from "react";
import { MdBusiness, MdSearch } from "react-icons/md";
import AjudaTela from "../../components/AjudaTela";
import { apiAdminPlataforma } from "../../services/apiAdminPlataforma";

const AJUDA = {
  titulo: "Cadastro de Empresas",
  descricao:
    "Cadastre aqui as empresas (contratos, por CNPJ ou CPF) que usam o sistema — este é o passo inicial para dar acesso a um cliente novo, seja uma empresa (pessoa jurídica) ou um produtor individual sem CNPJ (pessoa física). Ao cadastrar, um usuário admin já é criado automaticamente com a senha padrão, mostrada na tela em seguida. Esse usuário, ao logar na tela normal, cria a primeira fazenda da empresa e já entra no sistema como Proprietário.",
  secoes: [
    {
      titulo: "Dados da Empresa",
      campos: [
        {
          nome: "Tipo de pessoa",
          obrigatorio: true,
          explicacao: "'Jurídica' para empresa (documento é o CNPJ) ou 'Física' para produtor individual sem CNPJ (documento é o CPF). Define a máscara e o tamanho esperado do campo abaixo.",
        },
        {
          nome: "CNPJ/CPF",
          obrigatorio: true,
          explicacao: "Documento do contrato. Para pessoa jurídica, clique em 'Buscar' para preencher automaticamente a razão social a partir do CNPJ — esse recurso não existe para CPF. Precisa ser único — o sistema não permite dois contratos com o mesmo documento.",
        },
        {
          nome: "Razão Social",
          obrigatorio: true,
          explicacao: "Nome oficial da empresa. É o que aparece nas listagens administrativas.",
        },
        {
          nome: "Status",
          obrigatorio: true,
          explicacao: "'Ativo' permite login normalmente. 'Suspenso' ou 'Cancelado' bloqueiam o login de todos os usuários vinculados a essa empresa, mesmo com credenciais corretas.",
        },
      ],
    },
    {
      titulo: "Plano e Fazendas",
      campos: [
        {
          nome: "Plano",
          obrigatorio: true,
          explicacao: "Define o que a empresa contratou (Básico, Profissional ou Empresarial). Cada plano tem uma quantidade padrão de fazendas — escolher o plano já preenche essa quantidade, que pode ser ajustada em seguida.",
        },
        {
          nome: "Quantidade de fazendas",
          obrigatorio: true,
          explicacao: "Quantas fazendas essa empresa pode cadastrar no sistema. Vem preenchida com o padrão do plano escolhido, mas pode ser editada para dar um número diferente a este contrato específico. Reduzir abaixo do número de fazendas já cadastradas não é permitido.",
        },
      ],
    },
  ],
  dicas: [
    "Esta tela é de uso exclusivo da equipe Novalis Agro — os clientes não têm (e não devem ter) acesso a ela.",
    "Mudar o status para 'Suspenso' ou 'Cancelado' bloqueia o acesso de TODOS os usuários dessa empresa imediatamente no próximo login.",
    "A quantidade de fazendas pode ser alterada depois, editando a empresa — não precisa recadastrar nada.",
  ],
};

const STATUS_OPTIONS = [
  { value: "ATIVO", label: "Ativo", cor: "bg-green-100 text-green-800" },
  { value: "SUSPENSO", label: "Suspenso", cor: "bg-yellow-100 text-yellow-800" },
  { value: "CANCELADO", label: "Cancelado", cor: "bg-red-100 text-red-800" },
];

const TIPO_PESSOA_OPTIONS = [
  { value: "J", label: "Jurídica (CNPJ)" },
  { value: "F", label: "Física (CPF)" },
];

const CPF_MASK = (v) => {
  const n = v.replace(/\D/g, "").slice(0, 11);
  return n
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};

const CNPJ_MASK = (v) => {
  const n = v.replace(/\D/g, "").slice(0, 14);
  return n
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

// Formata o documento conforme o tipo de pessoa do contrato — usado tanto no
// input do formulário quanto na coluna da tabela de listagem.
const formatarDocumento = (valor, tipoPessoa) =>
  tipoPessoa === "F" ? CPF_MASK(valor || "") : CNPJ_MASK(valor || "");

export default function AdminPlataformaEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [credenciaisCriadas, setCredenciaisCriadas] = useState(null);

  const [formData, setFormData] = useState({
    cnpj: "",
    tipo_pessoa: "J",
    razao_social: "",
    status: "ATIVO",
    plano_id: "",
    quantidade_fazendas: "",
  });

  const carregarEmpresas = useCallback(async () => {
    try {
      setCarregando(true);
      setErro("");
      const response = await apiAdminPlataforma.get("/api/admin-plataforma/empresas/");
      const dados = response.data.results || response.data;
      setEmpresas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
      setErro("Erro ao carregar lista de empresas.");
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarPlanos = useCallback(async () => {
    try {
      const response = await apiAdminPlataforma.get("/api/admin-plataforma/planos/");
      const dados = response.data.results || response.data;
      setPlanos(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar planos:", error);
    }
  }, []);

  useEffect(() => {
    carregarEmpresas();
    carregarPlanos();
  }, [carregarEmpresas, carregarPlanos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cnpj") {
      setFormData((prev) => ({ ...prev, cnpj: formatarDocumento(value, prev.tipo_pessoa) }));
    } else if (name === "tipo_pessoa") {
      // Trocar o tipo de pessoa reformata (e trunca) o que já foi digitado
      // no campo de documento, em vez de deixar dígitos de sobra da máscara anterior.
      setFormData((prev) => ({
        ...prev,
        tipo_pessoa: value,
        cnpj: formatarDocumento(prev.cnpj, value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelecionarPlano = (plano) => {
    setFormData((prev) => ({
      ...prev,
      plano_id: String(plano.id),
      quantidade_fazendas: String(plano.limite_fazendas),
    }));
  };

  const buscarCnpj = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, "");
    if (cnpjLimpo.length !== 14) {
      alert("Informe um CNPJ completo (14 dígitos) antes de buscar.");
      return;
    }

    setBuscandoCnpj(true);
    try {
      const response = await apiAdminPlataforma.get(`/consulta_cnpj/${cnpjLimpo}/`);
      const razaoSocial = response.data?.razao_social;
      if (razaoSocial) {
        setFormData((prev) => ({ ...prev, razao_social: razaoSocial }));
      } else {
        alert("CNPJ encontrado, mas sem razão social retornada.");
      }
    } catch (error) {
      console.error("Erro ao buscar CNPJ:", error);
      alert(error.response?.data?.error || "Não foi possível consultar este CNPJ.");
    } finally {
      setBuscandoCnpj(false);
    }
  };

  const handleNovaEmpresa = () => {
    const primeiroPlano = planos[0];
    setFormData({
      cnpj: "",
      tipo_pessoa: "J",
      razao_social: "",
      status: "ATIVO",
      plano_id: primeiroPlano ? String(primeiroPlano.id) : "",
      quantidade_fazendas: primeiroPlano ? String(primeiroPlano.limite_fazendas) : "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(true);
  };

  const handleEditar = (empresa) => {
    const tipoPessoa = empresa.tipo_pessoa || "J";
    setFormData({
      cnpj: formatarDocumento(empresa.cnpj || "", tipoPessoa),
      tipo_pessoa: tipoPessoa,
      razao_social: empresa.razao_social || "",
      status: empresa.status || "ATIVO",
      plano_id: empresa.plano_atual?.id ? String(empresa.plano_atual.id) : "",
      quantidade_fazendas: String(empresa.limite_fazendas ?? 0),
    });
    setEditando(true);
    setEditandoId(empresa.id);
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setMostrarFormulario(false);
    setEditando(false);
    setEditandoId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.razao_social.trim()) {
      alert("Preencha a razão social.");
      return;
    }
    const digitosDocumento = formData.cnpj.replace(/\D/g, "");
    const tamanhoEsperado = formData.tipo_pessoa === "F" ? 11 : 14;
    if (digitosDocumento.length !== tamanhoEsperado) {
      alert(
        formData.tipo_pessoa === "F"
          ? "Informe um CPF válido (11 dígitos)."
          : "Informe um CNPJ válido (14 dígitos)."
      );
      return;
    }
    if (!formData.plano_id) {
      alert("Selecione um plano.");
      return;
    }
    const quantidadeFazendas = Number(formData.quantidade_fazendas);
    if (formData.quantidade_fazendas === "" || Number.isNaN(quantidadeFazendas) || quantidadeFazendas < 0) {
      alert("Informe uma quantidade de fazendas válida (0 ou mais).");
      return;
    }

    setSalvando(true);
    const payload = {
      cnpj: formData.cnpj,
      tipo_pessoa: formData.tipo_pessoa,
      razao_social: formData.razao_social,
      status: formData.status,
    };

    try {
      if (editando) {
        await apiAdminPlataforma.put(`/api/admin-plataforma/empresas/${editandoId}/`, payload);
        await apiAdminPlataforma.post(`/api/admin-plataforma/empresas/${editandoId}/ajustar-fazendas/`, {
          plano_id: formData.plano_id,
          quantidade_fazendas: quantidadeFazendas,
        });
      } else {
        const response = await apiAdminPlataforma.post("/api/admin-plataforma/empresas/", {
          ...payload,
          plano_id: formData.plano_id,
          quantidade_fazendas: quantidadeFazendas,
        });
        if (response.data?.usuario_admin) {
          setCredenciaisCriadas(response.data.usuario_admin);
        }
      }
      handleCancel();
      carregarEmpresas();
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (empresa) => {
    if (
      !window.confirm(
        `Tem certeza que deseja excluir "${empresa.razao_social}"? Isso apaga o contrato e TODOS os dados vinculados a ele (fazendas, usuários, lançamentos). Esta ação não pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      await apiAdminPlataforma.delete(`/api/admin-plataforma/empresas/${empresa.id}/`);
      carregarEmpresas();
    } catch (error) {
      console.error("Erro ao excluir empresa:", error);
      alert("Erro ao excluir empresa.");
    }
  };

  const getStatusInfo = (status) =>
    STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

  const formatarData = (data) => (data ? new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—");

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            Empresas <MdBusiness size={30} />
          </h2>
          <AjudaTela {...AJUDA} />
        </div>
        {!mostrarFormulario && (
          <button
            onClick={handleNovaEmpresa}
            className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2 rounded-lg transition-colors text-sm font-medium"
          >
            + Nova Empresa
          </button>
        )}
      </div>

      {erro && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 border border-red-200">
          {erro}
        </div>
      )}

      {credenciaisCriadas && (
        <div className="mb-4 p-4 rounded-lg bg-green-50 border border-green-300 flex justify-between items-start gap-4">
          <div className="text-sm text-green-900">
            <p className="font-semibold mb-1">Empresa cadastrada. Usuário admin criado:</p>
            <p>
              Usuário: <span className="font-mono font-bold">{credenciaisCriadas.username}</span>
              {" — "}
              Senha: <span className="font-mono font-bold">{credenciaisCriadas.senha}</span>
            </p>
            <p className="text-green-700 mt-1">
              Ao logar com esse usuário na tela normal, ele vai criar a primeira fazenda da empresa e já entra como Proprietário.
            </p>
          </div>
          <button
            onClick={() => setCredenciaisCriadas(null)}
            className="text-green-700 hover:text-green-900 text-sm font-medium"
          >
            Fechar
          </button>
        </div>
      )}

      {mostrarFormulario && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editando ? "Editar Empresa" : "Nova Empresa"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Tipo de pessoa *</label>
                <select
                  name="tipo_pessoa"
                  value={formData.tipo_pessoa}
                  onChange={handleChange}
                  className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                >
                  {TIPO_PESSOA_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {formData.tipo_pessoa === "F" ? "CPF *" : "CNPJ *"}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={handleChange}
                    placeholder={formData.tipo_pessoa === "F" ? "000.000.000-00" : "00.000.000/0001-00"}
                    className="flex-1 border border-gray-300 p-2 rounded focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                    required
                  />
                  {formData.tipo_pessoa !== "F" && (
                    <button
                      type="button"
                      onClick={buscarCnpj}
                      disabled={buscandoCnpj}
                      className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-3 rounded flex items-center gap-1 text-sm"
                      title="Buscar razão social pelo CNPJ"
                    >
                      <MdSearch size={18} />
                      {buscandoCnpj ? "..." : "Buscar"}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
                <input
                  type="text"
                  name="razao_social"
                  value={formData.razao_social}
                  onChange={handleChange}
                  className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                  required
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Plano *</label>
              {planos.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nenhum plano cadastrado ainda. Cadastre ao menos um plano antes de criar empresas.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {planos.map((plano) => {
                    const selecionado = String(plano.id) === String(formData.plano_id);
                    return (
                      <button
                        key={plano.id}
                        type="button"
                        onClick={() => handleSelecionarPlano(plano)}
                        className={`text-left p-3 rounded-lg border-2 transition-colors ${
                          selecionado
                            ? "border-gray-800 bg-gray-50"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <p className="font-semibold text-sm text-gray-800">{plano.nome}</p>
                        <p className="text-xs text-gray-500 mb-1">
                          até {plano.limite_fazendas} fazenda(s) por padrão
                        </p>
                        {plano.descricao && (
                          <p className="text-xs text-gray-600">{plano.descricao}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col max-w-xs">
              <label className="text-sm font-medium text-gray-700 mb-1">Quantidade de fazendas *</label>
              <input
                type="number"
                name="quantidade_fazendas"
                min="0"
                value={formData.quantidade_fazendas}
                onChange={handleChange}
                className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Preenchida com o padrão do plano selecionado — pode ser alterada.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={salvando}
                className="bg-gray-800 hover:bg-gray-900 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors text-sm font-medium"
              >
                {salvando ? "Salvando..." : editando ? "Atualizar" : "Cadastrar"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <p className="text-gray-600 text-sm">{empresas.length} empresa(s) cadastrada(s)</p>
        </div>

        {carregando ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">CNPJ/CPF</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Razão Social</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Plano</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazendas</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Criado em</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((empresa) => {
                  const statusInfo = getStatusInfo(empresa.status);
                  return (
                    <tr key={empresa.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="p-3 text-sm font-mono">
                        {formatarDocumento(empresa.cnpj, empresa.tipo_pessoa)}
                      </td>
                      <td className="p-3 text-sm font-medium">{empresa.razao_social}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.cor}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-3 text-sm">{empresa.plano_atual?.nome || "—"}</td>
                      <td className="p-3 text-sm">
                        {empresa.fazendas_cadastradas ?? 0}/{empresa.limite_fazendas ?? 0}
                      </td>
                      <td className="p-3 text-sm">{formatarData(empresa.criado_em)}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditar(empresa)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleExcluir(empresa)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {empresas.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      Nenhuma empresa cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
